import { useEffect, useMemo, useRef, useState } from "react";
import moment from "moment";
import { Bar } from "react-chartjs-2";
import "chart.js/auto";
import StatsDateSelector, { getMinAllowedDate } from "./StatsDateSelector";
import { applyTimeZone, fetchAPI, isAssaigDeMusics, postAPI } from "../../utils/utils";
import { getPersistentItem, setPersistentItem } from "../../utils/storage";
import { requestMissingProvesBatch } from "./statsProvesData";
import { TempsCollaPerEvent, useTempsColla } from "./TempsCollaCharts";

const COLORS = {
    pinya: '#3b82f6',
    terra: '#f59e0b', 
    netes: '#10b981',
    neutral: '#64748b'
};

const getBundleType = bundle => {
    if (!bundle || !bundle.parts) return null;

    const parts = Object.keys(bundle.parts)
        .map(part => part.toLowerCase())
        .filter(part => ['pinya', 'folre', 'manilles', 'puntals', 'tronc'].includes(part));

    if (parts.length === 0) return null;

    if (parts.includes('pinya')) return 'pinya';

    if (parts.every(part => part === 'tronc')) return 'netes';

    return 'terra';
};

const formatAverage = (value, totalAssaigs) => {
    if (totalAssaigs === 0) return '—';
    return (value / totalAssaigs).toFixed(2);
};

const StatCard = ({ label, value, accent, subtitle }) => (
    <div style={{
        padding: '20px 24px',
        borderLeft: accent ? `3px solid ${accent}` : '3px solid transparent',
        background: '#fff',
    }}>
        <div style={{
            fontSize: 32,
            fontWeight: 600,
            color: '#1e293b',
            fontVariantNumeric: 'tabular-nums',
            letterSpacing: '-0.02em',
            lineHeight: 1.1
        }}>
            {value}
        </div>
        <div style={{
            fontSize: 13,
            color: '#64748b',
            marginTop: 6,
            fontWeight: 500,
            textTransform: 'uppercase',
            letterSpacing: '0.04em'
        }}>
            {label}
        </div>
        {subtitle ? (
            <div style={{
                fontSize: 12,
                color: '#94a3b8',
                marginTop: 4
            }}>
                {subtitle}
            </div>
        ) : null}
    </div>
);

const STORAGE_KEY_HIDDEN_IDS = 'stats-assaigs-hidden-ids';
const STORAGE_KEY_HIDDEN_TITLES = 'stats-assaigs-hidden-titles';

const loadFromStorage = (key, defaultValue) => {
    try {
        const stored = getPersistentItem(key);
        return stored ? JSON.parse(stored) : defaultValue;
    } catch {
        return defaultValue;
    }
};

function StatsAssaigsTab({ events, castellersInfo, filtersEvents }) {
    const [start_date, setStart_date] = useState(getMinAllowedDate)
    const [end_date, setEnd_date] = useState(moment().endOf('year').format('YYYY-MM-DD'))
    const [bundles, setBundles] = useState([]);
    const [provesByEvent, setProvesByEvent] = useState({});
    const [hiddenIds, setHiddenIds] = useState(() => loadFromStorage(STORAGE_KEY_HIDDEN_IDS, []));
    const [hiddenTitles, setHiddenTitles] = useState(() => loadFromStorage(STORAGE_KEY_HIDDEN_TITLES, []));
    const pendingProvesEventIds = useRef(new Set());

    useEffect(() => {
        fetchAPI('/bundles', setBundles);
    }, []);

    // Persist hiddenIds to localStorage
    useEffect(() => {
        setPersistentItem(STORAGE_KEY_HIDDEN_IDS, JSON.stringify(hiddenIds));
    }, [hiddenIds]);

    // Persist hiddenTitles to localStorage
    useEffect(() => {
        setPersistentItem(STORAGE_KEY_HIDDEN_TITLES, JSON.stringify(hiddenTitles));
    }, [hiddenTitles]);

    const bundlesById = useMemo(() => {
        return bundles.reduce((acc, bundle) => {
            acc[bundle.id] = bundle;
            return acc;
        }, {});
    }, [bundles]);

    // All assaigs in date range (before hiding)
    const allAssaigsInRange = useMemo(() => {
        return events
            .filter(event => event.tipus === 'assaig')
            .filter(event => !isAssaigDeMusics(event))
            .filter(event => moment(applyTimeZone(event['data-esperada-inici'])).isBetween(start_date, end_date, 'day', '[]'))
            .sort((a, b) => moment(a['data-esperada-inici']).diff(moment(b['data-esperada-inici'])));
    }, [events, start_date, end_date]);

    // Filtered assaigs (after hiding)
    const filteredAssaigs = useMemo(() => {
        return allAssaigsInRange
            .filter(event => !hiddenIds.includes(event.id))
            .filter(event => !hiddenTitles.includes(event.title));
    }, [allAssaigsInRange, hiddenIds, hiddenTitles]);

    // All assaigs with at least 1 prova (for filtering UI)
    const allAssaigsAmbProves = useMemo(() => {
        return allAssaigsInRange.filter(event => 
            provesByEvent[event.id]?.total > 0
        );
    }, [allAssaigsInRange, provesByEvent]);

    // Get unique titles for the "hide by name" feature, sorted by recurrence descending
    const uniqueTitles = useMemo(() => {
        const titleCounts = {};
        allAssaigsAmbProves.forEach(e => {
            titleCounts[e.title] = (titleCounts[e.title] || 0) + 1;
        });
        const titles = [...new Set(allAssaigsAmbProves.map(e => e.title))];
        return titles.sort((a, b) => titleCounts[b] - titleCounts[a]);
    }, [allAssaigsAmbProves]);

    useEffect(() => {
        const eventIds = allAssaigsInRange.map(event => event.id);
        if (eventIds.length === 0 || bundles.length === 0) {
            setProvesByEvent({});
            return;
        }

        requestMissingProvesBatch({
            eventIds,
            provesByEvent,
            pendingEventIds: pendingProvesEventIds.current,
            postAPI,
            onLoaded: loadedProves => {
                const loadedCounts = Object.entries(loadedProves).reduce((result, [eventId, proves]) => {
                    result[eventId] = proves.reduce((acc, prova) => {
                        const bundleId = prova.split('.')[0];
                        const bundleType = getBundleType(bundlesById[bundleId]);

                        if (bundleType) {
                            acc[bundleType] += 1;
                        }

                        acc.total += 1;
                        return acc;
                    }, { pinya: 0, terra: 0, netes: 0, total: 0 });
                    return result;
                }, {});

                setProvesByEvent(previous => ({ ...previous, ...loadedCounts }));
            },
        });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [allAssaigsInRange, bundlesById, bundles.length]);

    // Només els assaigs que ja han començat: dels futurs no se sap què s'hi farà ni qui hi vindrà
    const assaigsPassatsIds = useMemo(() => allAssaigsInRange
        .filter(event => moment(event['data-esperada-inici']).isBefore(moment()))
        .map(event => event.id),
    [allAssaigsInRange]);
    const tempsColla = useTempsColla(assaigsPassatsIds);

    // Assaigs passats amb almenys 1 prova (per estadístiques i gràfic)
    const assaigsAmbProves = useMemo(() => {
        return filteredAssaigs
            .filter(event => assaigsPassatsIds.includes(event.id))
            .filter(event => provesByEvent[event.id]?.total > 0);
    }, [filteredAssaigs, assaigsPassatsIds, provesByEvent]);

    const chartData = useMemo(() => {
        const labels = assaigsAmbProves.map(event => `${moment(event['data-esperada-inici']).format('DD/MM/YY')} ${event.title}`);

        return {
            labels,
            datasets: [
                {
                    label: 'Amb pinya',
                    data: assaigsAmbProves.map(event => provesByEvent[event.id]?.pinya || 0),
                    backgroundColor: COLORS.pinya,
                    borderRadius: 2
                },
                {
                    label: 'A terra',
                    data: assaigsAmbProves.map(event => provesByEvent[event.id]?.terra || 0),
                    backgroundColor: COLORS.terra,
                    borderRadius: 2
                },
                {
                    label: 'Netes',
                    data: assaigsAmbProves.map(event => provesByEvent[event.id]?.netes || 0),
                    backgroundColor: COLORS.netes,
                    borderRadius: 2
                }
            ]
        };
    }, [assaigsAmbProves, provesByEvent]);

    const totals = useMemo(() => {
        return assaigsAmbProves.reduce((acc, event) => {
            const counts = provesByEvent[event.id] || { pinya: 0, terra: 0, netes: 0, total: 0 };
            acc.pinya += counts.pinya;
            acc.terra += counts.terra;
            acc.netes += counts.netes;
            acc.total += counts.total;
            return acc;
        }, { pinya: 0, terra: 0, netes: 0, total: 0 });
    }, [assaigsAmbProves, provesByEvent]);

    const options = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            // Les etiquetes (plugin registrat globalment) només per a franges amb proves
            datalabels: {
                display: ctx => ctx.dataset.data[ctx.dataIndex] > 0
            },
            legend: {
                position: 'bottom',
                labels: {
                    usePointStyle: true,
                    pointStyle: 'rectRounded',
                    padding: 20,
                    font: {
                        size: 12,
                        weight: 500
                    }
                }
            },
            tooltip: {
                backgroundColor: '#1e293b',
                titleFont: { size: 12, weight: 500 },
                bodyFont: { size: 12 },
                padding: 12,
                cornerRadius: 6,
                displayColors: true,
                boxPadding: 4
            }
        },
        scales: {
            x: {
                stacked: true,
                grid: {
                    display: false
                },
                border: {
                    display: false
                },
                ticks: {
                    callback: function(index) {
                        const label = chartData.labels?.[index];
                        return label?.split(' ')?.[0] ?? ''
                    },
                    font: { size: 11 },
                    color: '#94a3b8',
                    maxRotation: 45,
                    minRotation: 45
                }
            },
            y: {
                stacked: true,
                beginAtZero: true,
                grid: {
                    color: '#f1f5f9'
                },
                border: {
                    display: false
                },
                ticks: {
                    font: { size: 11 },
                    color: '#94a3b8',
                    padding: 8
                }
            }
        }
    };

    return (
        <div>
            <StatsDateSelector
                start_date={start_date}
                setStart_date={setStart_date}
                end_date={end_date}
                setEnd_date={setEnd_date}
            />

            <section style={{ marginTop: 32 }}>
                <h2 style={{ 
                    fontSize: 15, 
                    fontWeight: 600, 
                    color: '#334155',
                    marginBottom: 16,
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em'
                }}>
                    Proves per assaig
                </h2>
                {filteredAssaigs.length === 0 ? (
                    <p style={{ color: '#64748b', fontSize: 14 }}>
                        No hi ha assaigs dins del rang de dates seleccionat.
                    </p>
                ) : (
                    <div style={{ 
                        width: '100%', 
                        height: 320,
                        background: '#fff',
                        borderRadius: 8,
                        border: '1px solid #e2e8f0',
                        padding: '24px 16px 16px 8px'
                    }}>
                        <Bar data={chartData} options={options} />
                    </div>
                )}
            </section>

            {tempsColla && assaigsAmbProves.length > 0 ? (
                <section style={{ marginTop: 40 }}>
                    <h2 style={{
                        fontSize: 15,
                        fontWeight: 600,
                        color: '#334155',
                        marginBottom: 16,
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em'
                    }}>
                        Temps a la colla dels assistents
                    </h2>
                    <TempsCollaPerEvent
                        events={assaigsAmbProves}
                        tempsColla={tempsColla}
                        castellersInfo={castellersInfo}
                        filters={filtersEvents}
                        height={260}
                    />
                </section>
            ) : null}

            <section style={{ marginTop: 40 }}>
                <h2 style={{ 
                    fontSize: 15, 
                    fontWeight: 600, 
                    color: '#334155',
                    marginBottom: 16,
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em'
                }}>
                    Resum
                </h2>
                
                {/* Primary stats row */}
                <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
                    border: '1px solid #e2e8f0',
                    borderRadius: 8,
                    overflow: 'hidden',
                    background: '#fff'
                }}>
                    <StatCard 
                        label="Assaigs" 
                        value={assaigsAmbProves.length}
                    />
                    <StatCard 
                        label="Total proves" 
                        value={totals.total}
                    />
                    <StatCard 
                        label="Amb pinya" 
                        value={totals.pinya}
                        accent={COLORS.pinya}
                    />
                    <StatCard 
                        label="A terra" 
                        value={totals.terra}
                        accent={COLORS.terra}
                    />
                    <StatCard 
                        label="Netes" 
                        value={totals.netes}
                        accent={COLORS.netes}
                    />
                </div>

                {/* Averages row */}
                <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
                    border: '1px solid #e2e8f0',
                    borderRadius: 8,
                    overflow: 'hidden',
                    background: '#fff',
                    marginTop: 12
                }}>
                    <StatCard 
                        label="Mitjana total" 
                        value={formatAverage(totals.total, assaigsAmbProves.length)}
                        subtitle="proves / assaig"
                    />
                    <StatCard 
                        label="Mitjana pinya" 
                        value={formatAverage(totals.pinya, assaigsAmbProves.length)}
                        accent={COLORS.pinya}
                        subtitle="proves / assaig"
                    />
                    <StatCard 
                        label="Mitjana terra" 
                        value={formatAverage(totals.terra, assaigsAmbProves.length)}
                        accent={COLORS.terra}
                        subtitle="proves / assaig"
                    />
                    <StatCard 
                        label="Mitjana netes" 
                        value={formatAverage(totals.netes, assaigsAmbProves.length)}
                        accent={COLORS.netes}
                        subtitle="proves / assaig"
                    />
                </div>
            </section>

            <section style={{ marginTop: 40 }}>
                <h2 style={{ 
                    fontSize: 15, 
                    fontWeight: 600, 
                    color: '#334155',
                    marginBottom: 16,
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em'
                }}>
                    Filtrar assaigs
                </h2>

                {/* Assaig list */}
                <div style={{
                    background: '#fff',
                    border: '1px solid #e2e8f0',
                    borderRadius: 8,
                    overflow: 'hidden'
                }}>
                    <div style={{ 
                        padding: '12px 16px',
                        borderBottom: '1px solid #e2e8f0',
                        background: '#f8fafc'
                    }}>
                        <span style={{ 
                            fontSize: 12, 
                            fontWeight: 600, 
                            color: '#64748b',
                            textTransform: 'uppercase',
                            letterSpacing: '0.04em'
                        }}>
                            Amagar per nom
                        </span>
                    </div>
                    <div style={{ 
                        display: 'flex', 
                        flexWrap: 'wrap', 
                        gap: 8,
                        padding: 16
                    }}>
                        {uniqueTitles.map(title => {
                            const isHidden = hiddenTitles.includes(title);
                            const count = allAssaigsAmbProves.filter(e => e.title === title).length;
                            return (
                                <button
                                    key={title}
                                    onClick={() => {
                                        if (isHidden) {
                                            setHiddenTitles(prev => prev.filter(t => t !== title));
                                        } else {
                                            setHiddenTitles(prev => [...prev, title]);
                                            // Remove individual hides for this title
                                            const idsWithTitle = allAssaigsAmbProves.filter(e => e.title === title).map(e => e.id);
                                            setHiddenIds(prev => prev.filter(id => !idsWithTitle.includes(id)));
                                        }
                                    }}
                                    style={{
                                        padding: '6px 12px',
                                        fontSize: 13,
                                        background: isHidden ? '#f1f5f9' : '#fff',
                                        border: '1px solid #e2e8f0',
                                        borderRadius: 6,
                                        color: isHidden ? '#94a3b8' : '#334155',
                                        cursor: 'pointer',
                                        textDecoration: isHidden ? 'line-through' : 'none',
                                        opacity: isHidden ? 0.6 : 1
                                    }}
                                >
                                    {title}
                                    <span style={{ 
                                        marginLeft: 6, 
                                        fontSize: 11, 
                                        color: '#94a3b8',
                                        fontWeight: 500
                                    }}>
                                        ×{count}
                                    </span>
                                </button>
                            );
                        })}
                    </div>

                    <div style={{ 
                        padding: '12px 16px',
                        borderTop: '1px solid #e2e8f0',
                        borderBottom: '1px solid #e2e8f0',
                        background: '#f8fafc'
                    }}>
                        <span style={{ 
                            fontSize: 12, 
                            fontWeight: 600, 
                            color: '#64748b',
                            textTransform: 'uppercase',
                            letterSpacing: '0.04em'
                        }}>
                            Amagar individualment
                        </span>
                    </div>
                    <div style={{ 
                        maxHeight: 300,
                        overflowY: 'auto'
                    }}>
                        {allAssaigsAmbProves.map(event => {
                            const isHiddenById = hiddenIds.includes(event.id);
                            const isHiddenByTitle = hiddenTitles.includes(event.title);
                            const isHidden = isHiddenById || isHiddenByTitle;
                            
                            return (
                                <div
                                    key={event.id}
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        padding: '10px 16px',
                                        borderBottom: '1px solid #f1f5f9',
                                        background: isHidden ? '#f8fafc' : '#fff',
                                        opacity: isHidden ? 0.5 : 1
                                    }}
                                >
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                                        <span style={{ 
                                            fontSize: 12, 
                                            color: '#94a3b8',
                                            fontVariantNumeric: 'tabular-nums',
                                            minWidth: 50
                                        }}>
                                            {moment(event['data-esperada-inici']).format('DD/MM')}
                                        </span>
                                        <span style={{ 
                                            fontSize: 14, 
                                            color: '#334155',
                                            textDecoration: isHidden ? 'line-through' : 'none'
                                        }}>
                                            {event.title}
                                        </span>
                                        {isHiddenByTitle ? (
                                            <span style={{ 
                                                fontSize: 11, 
                                                color: '#94a3b8',
                                                fontStyle: 'italic'
                                            }}>
                                                (amagat per nom)
                                            </span>
                                        ) : null}
                                    </div>
                                    {!isHiddenByTitle ? (
                                        <button
                                            onClick={() => {
                                                if (isHiddenById) {
                                                    setHiddenIds(prev => prev.filter(id => id !== event.id));
                                                } else {
                                                    setHiddenIds(prev => [...prev, event.id]);
                                                }
                                            }}
                                            style={{
                                                padding: '4px 10px',
                                                fontSize: 12,
                                                background: isHiddenById ? '#10b981' : '#f1f5f9',
                                                border: 'none',
                                                borderRadius: 4,
                                                color: isHiddenById ? '#fff' : '#64748b',
                                                cursor: 'pointer',
                                                fontWeight: 500
                                            }}
                                        >
                                            {isHiddenById ? 'Mostrar' : 'Amagar'}
                                        </button>
                                    ) : null}
                                </div>
                            );
                        })}
                    </div>
                </div>
            </section>
        </div>
    );
}

export default StatsAssaigsTab;
